const WAVELENGTH = 1440; // fallback wave length (px)
const REF_WIDTH = 1920; // speed ref: cross this width in `speed` seconds
const NS = 'http://www.w3.org/2000/svg';
const NBSP = ' '; // regular spaces would collapse in SVG text

let uid = 0; // unique <defs> path id per instance

class OilogicCurveMarquee extends HTMLElement {
  #ctrl = new AbortController();
  #svg = null;
  #path = null; // <defs> geometry, used for getPointAtLength
  #textPath = null;
  #sprites = []; // { el, arcBase }
  #buildRaf = 0;
  #animRaf = 0;
  #lastW = 0;

  connectedCallback() {
    document.fonts.ready.then(() => {
      if (this.isConnected) this.#build();
    });

    // iOS fires resize on URL-bar collapse; rebuild only on width change.
    const onResize = () => {
      const w = this.getBoundingClientRect().width;
      if (w && w !== this.#lastW) this.#scheduleBuild();
    };
    window.addEventListener('resize', onResize, { signal: this.#ctrl.signal });
    document.addEventListener('shopify:section:load', () => this.#scheduleBuild(), { signal: this.#ctrl.signal });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#buildRaf);
    cancelAnimationFrame(this.#animRaf);
    this.#ctrl.abort();
  }

  #scheduleBuild() {
    cancelAnimationFrame(this.#buildRaf);
    this.#buildRaf = requestAnimationFrame(() => this.#build());
  }

  #teardown() {
    cancelAnimationFrame(this.#animRaf);
    this.#svg?.remove();
    this.#svg = null;
    this.#path = null;
    this.#textPath = null;
    this.#sprites = [];
  }

  #readConfig() {
    return {
      bandColor: this.dataset.bandColor || '#afdfe1',
      textColor: this.dataset.textColor || '#ffffff',
      speed: Math.max(5, parseInt(this.dataset.speed, 10) || 30),
      fontSize: Math.max(10, parseInt(this.dataset.fontSize, 10) || 24),
      fontWeight: this.dataset.fontWeight || '600',
      letterSpacing: parseFloat(this.dataset.letterSpacing || '0') * 0.01 + 'em',
      textTransform: this.dataset.textTransform || 'none',
      amplitude: Math.max(5, parseInt(this.dataset.amplitude, 10) || 65),
      thickness: Math.max(20, parseInt(this.dataset.bandThickness, 10) || 110),
      wavelength: Math.max(400, parseInt(this.dataset.waveLength, 10) || WAVELENGTH),
    };
  }

  #readItems() {
    return Array.from(this.querySelectorAll('.curve-marquee__items > li'))
      .map((li) => ({
        iconUrl: li.dataset.icon || '',
        text: (li.textContent || '').trim(),
      }))
      .filter((item) => item.text);
  }

  // Sinusoidal wave path across `loopW` (an integer number of wavelengths)
  #wavePath(loopW, centerY, amplitude, wavelength) {
    const cp = amplitude * (4 / 3); // cubic control offset approximating a sine half-arc
    const cycles = Math.round(loopW / wavelength);
    let d = `M 0 ${centerY}`;
    for (let i = 0; i < cycles; i++) {
      const x = i * wavelength;
      d += ` C ${x + wavelength * 0.1667} ${centerY - cp} ${x + wavelength * 0.3333} ${centerY - cp} ${x + wavelength * 0.5} ${centerY}`;
      d += ` C ${x + wavelength * 0.6667} ${centerY + cp} ${x + wavelength * 0.8333} ${centerY + cp} ${x + wavelength} ${centerY}`;
    }
    return d;
  }

  #build() {
    this.#teardown();

    const cfg = this.#readConfig();
    const items = this.#readItems();
    if (!items.length) return;

    // 1:1 user-unit = CSS px space, so text metrics and path queries line up.
    const viewW = this.getBoundingClientRect().width || window.innerWidth;
    this.#lastW = viewW;

    // Figma mode: ride the Vector 254 stroke (data-wave-path[-mobile]) scaled
    // from its design width. Custom mode: generated sine.
    const desktop = window.matchMedia('(min-width: 768px)').matches;
    if (!desktop) {
      // The design's mobile marquee has its own type/band sizes.
      cfg.fontSize = Math.max(10, parseInt(this.dataset.fontSizeMobile, 10) || cfg.fontSize);
      cfg.thickness = Math.max(20, parseInt(this.dataset.bandThicknessMobile, 10) || cfg.thickness);
    }
    const figmaD = desktop ? this.dataset.wavePath : this.dataset.wavePathMobile;
    let pathD;
    let loopW;
    let svgH;
    let hostH = 0;
    let topY = 0; // y-shift making room above the centerline for stroke + text
    if (figmaD) {
      const [refW, refH] = (desktop ? this.dataset.waveRef : this.dataset.waveRefMobile).split(' ').map(Number);
      const s = viewW / refW;
      cfg.fontSize = Math.round(cfg.fontSize * s);
      cfg.thickness = Math.round(cfg.thickness * s);
      pathD = figmaD.replace(/-?\d*\.?\d+/g, (n) => (n * s).toFixed(2));
      topY = cfg.thickness / 2 + cfg.fontSize;
      svgH = Math.ceil(refH * s + topY * 2);
      loopW = viewW;
      // Figma section height (host box); the centered SVG crops evenly.
      const designH = parseFloat(desktop ? this.dataset.bandHeight : this.dataset.bandHeightMobile);
      if (designH) hostH = Math.round(designH * s);
    } else {
      svgH = (cfg.amplitude + cfg.thickness / 2) * 2 + cfg.fontSize * 2;
      loopW = (Math.ceil(viewW / cfg.wavelength) + 1) * cfg.wavelength;
      pathD = this.#wavePath(loopW, svgH / 2, cfg.amplitude, cfg.wavelength);
    }
    // Band height to CSS: the design's section height when known, else the SVG's.
    this.style.setProperty('--curve-marquee-height', `${hostH || svgH}px`);
    const pathId = `cm-wave-${++uid}`;

    // SVG skeleton: geometry in <defs>, ribbon via <use>, flowing text, icons.
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'curve-marquee__band');
    svg.setAttribute('viewBox', `0 0 ${loopW} ${svgH}`);
    svg.setAttribute('width', String(loopW));
    svg.setAttribute('height', String(svgH));
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');

    const defs = document.createElementNS(NS, 'defs');
    const defPath = document.createElementNS(NS, 'path');
    defPath.setAttribute('id', pathId);
    defPath.setAttribute('d', pathD);
    defs.append(defPath);
    svg.append(defs);

    // One group so the Figma topY shift moves ribbon + text + icons together.
    const layer = document.createElementNS(NS, 'g');
    if (topY) layer.setAttribute('transform', `translate(0 ${topY})`);
    svg.append(layer);

    const ribbon = document.createElementNS(NS, 'use');
    ribbon.setAttribute('href', `#${pathId}`);
    ribbon.setAttribute('fill', 'none');
    ribbon.setAttribute('stroke', cfg.bandColor);
    ribbon.setAttribute('stroke-width', String(cfg.thickness));
    ribbon.setAttribute('stroke-linecap', 'round');
    layer.append(ribbon);

    const text = document.createElementNS(NS, 'text');
    text.setAttribute('class', 'curve-marquee__flow');
    text.setAttribute('fill', cfg.textColor);
    text.setAttribute('dominant-baseline', 'middle');
    text.style.fontSize = `${cfg.fontSize}px`;
    text.style.fontWeight = cfg.fontWeight;
    text.style.letterSpacing = cfg.letterSpacing;
    text.style.textTransform = cfg.textTransform;

    const textPath = document.createElementNS(NS, 'textPath');
    textPath.setAttribute('href', `#${pathId}`);
    textPath.setAttribute('startOffset', '0');
    text.append(textPath);
    layer.append(text);

    const iconLayer = document.createElementNS(NS, 'g');
    layer.append(iconLayer);

    this.append(svg);
    this.#svg = svg;
    this.#path = defPath;
    this.#textPath = textPath;

    // Figma: gap ≈ 2.4× font size (as NBSP glyph runs — WebKit mismeasures
    // `dx` on textPath), icon centered in the gap before its own text.
    const GAP = Math.round(cfg.fontSize * 2.4);
    const ICON_W = Math.round(cfg.fontSize * 1.15); // Figma: ~34px icon at 29.4px text

    // No text metrics (ancient browsers): one static set, glyphs still curve.
    if (typeof textPath.getComputedTextLength !== 'function') {
      textPath.textContent = items.map((it) => it.text).join(NBSP + NBSP + NBSP);
      return;
    }

    // Probe <text> (same styles, off the path) sizes the NBSP gap run.
    const probe = document.createElementNS(NS, 'text');
    probe.setAttribute('class', 'curve-marquee__flow');
    probe.style.cssText = text.style.cssText;
    probe.style.visibility = 'hidden';
    probe.textContent = NBSP.repeat(8);
    svg.append(probe);
    const nbspW = probe.getComputedTextLength() / 8;
    probe.remove();
    if (!nbspW) return;
    const gapChars = Math.max(1, Math.round(GAP / nbspW));
    const gapRun = NBSP.repeat(gapChars);

    // One set = text gap text gap … (the trailing gap chains the next set).
    const setStr = items.map((it) => it.text).join(gapRun) + gapRun;
    textPath.textContent = setStr;
    const repeatLen = textPath.getComputedTextLength();
    if (!repeatLen) return;

    // WebKit mishandles NEGATIVE startOffset on textPath (iOS text lagged the
    // icons): straight off-screen lead-in one loop long keeps offsets ≥ 0.
    const lead = Math.ceil(repeatLen);
    const p0 = defPath.getPointAtLength(0);
    defPath.setAttribute('d', `M ${(p0.x - lead).toFixed(2)} ${p0.y.toFixed(2)} L` + pathD.slice(1));
    const arcLen = defPath.getTotalLength();
    const repeats = Math.ceil(arcLen / repeatLen) + 2; // extra sets cover both edges
    textPath.textContent = setStr.repeat(repeats);

    // Icon anchor = the middle NBSP of the gap run before the item's text
    // (item 0 rides the previous set's trailing gap → negative on set 0).
    const anchorChar = [];
    let cpos = 0;
    for (let i = 0; i < items.length; i++) {
      anchorChar.push(cpos - gapChars + (gapChars >> 1));
      cpos += items[i].text.length + gapChars;
    }
    const setChars = setStr.length;

    for (let r = 0; r < repeats; r++) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].iconUrl) continue;
        const ci = r * setChars + anchorChar[i];
        if (ci < 0) continue;
        const img = document.createElementNS(NS, 'image');
        img.setAttribute('href', items[i].iconUrl);
        img.setAttribute('width', String(ICON_W));
        img.setAttribute('height', String(ICON_W));
        iconLayer.append(img);
        this.#sprites.push({ el: img, charIndex: ci });
      }
    }

    // Each frame asks the engine where it painted the anchor NBSP, so icons
    // sit wherever the glyphs actually are.
    const iconHalf = ICON_W / 2;
    const fs = cfg.fontSize;
    const place = (off) => {
      this.#textPath.setAttribute('startOffset', String(off));
      for (const sprite of this.#sprites) {
        let pos;
        let rot;
        try {
          pos = this.#textPath.getStartPositionOfChar(sprite.charIndex);
          rot = this.#textPath.getRotationOfChar(sprite.charIndex);
        } catch (e) {
          sprite.el.style.display = 'none';
          continue;
        }
        // Off-path chars clamp to an endpoint; show only in-viewport copies.
        if (pos.x <= 0 || pos.x > viewW + fs) {
          sprite.el.style.display = 'none';
          continue;
        }
        sprite.el.style.display = '';
        sprite.el.setAttribute(
          'transform',
          `translate(${pos.x.toFixed(2)} ${pos.y.toFixed(2)}) rotate(${rot.toFixed(2)}) translate(${-iconHalf} ${-iconHalf})`
        );
      }
    };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      place(repeatLen / 2);
      return;
    }

    const duration = (loopW / REF_WIDTH) * cfg.speed;
    const pxPerSecond = loopW / duration;
    const start = performance.now();

    const tick = (now) => {
      if (!this.isConnected) return;
      const elapsed = (now - start) / 1000;
      // Decreasing positive offset = leftward drift; the wrap is invisible
      // because the content repeats with that period.
      const off = repeatLen - ((elapsed * pxPerSecond) % repeatLen);
      place(off);
      this.#animRaf = requestAnimationFrame(tick);
    };
    this.#animRaf = requestAnimationFrame(tick);
  }
}

customElements.define('oilogic-curve-marquee', OilogicCurveMarquee);
